/**
 * FELVORA — Concepto de ACTIVIDAD VULNERABLE (feb 2026 · iter 08-01).
 *
 * Agrupa todos los giros de sujetos obligados bajo la LFPIORPI Art. 17 (reportan al SAT-UIF)
 * y los distingue de las entidades financieras supervisadas por la CNBV (SOFOM, etc.).
 *
 * Espejo del backend `actividad_vulnerable.py`. Cuando se agregue un giro LFPIORPI nuevo,
 * añadirlo en AMBOS archivos:
 *   1. `/app/backend/actividad_vulnerable.py` → set `ACTIVIDADES_VULNERABLES` + entrada
 *      en `GIRO_METADATA`.
 *   2. Este archivo → array `ACTIVIDADES_VULNERABLES` + entrada en `GIRO_METADATA`.
 *
 * ago-2026 · Fase 3.3 — espejo puesto al día con las 15 actividades del Art. 17 que la
 * plataforma opera (17 fracciones: inmobiliaria cubre V, V Bis y XV).
 *
 * REGLA DE ORO: SOFOM y demás CNBV → devuelven `false`. No se ven afectados.
 */

export const ACTIVIDADES_VULNERABLES = [
  "inmobiliaria",            // Fr. V / V Bis / XV — inmuebles.
  "joyeria",                 // Fr. VI   — metales y piedras preciosas, joyas y relojes.
  "arte",                    // Fr. VII  — subasta y comercialización de obras de arte.
  "vehiculos",               // Fr. VIII — comercialización y distribución de vehículos.
  "juegos_sorteos",          // Fr. I    — juegos con apuesta, concursos y sorteos.
  "tarjetas_servicios",      // Fr. II   — tarjetas de crédito/servicios, prepagadas, vales.
  "cheques_viajero",         // Fr. III  — cheques de viajero.
  "mutuo_prestamo",          // Fr. IV   — mutuo, préstamo o crédito no financiero.
  "blindaje",                // Fr. IX   — blindaje de vehículos e inmuebles.
  "traslado_valores",        // Fr. X    — traslado y custodia de dinero o valores.
  "servicios_profesionales", // Fr. XI   — servicios profesionales independientes.
  "fe_publica",              // Fr. XII  — notarios y corredores públicos.
  "donativos",               // Fr. XIII — donativos de organizaciones sin fines de lucro.
  "comercio_exterior",       // Fr. XIV  — agente o agencia aduanal.
  "activos_virtuales",       // Fr. XVI  — operaciones con activos virtuales.
];

/**
 * Catálogo de METADATA por giro. Los textos visibles al usuario se resuelven aquí para
 * evitar que el nombre del giro quede clavado en cada pantalla.
 *
 * Contrato de campos (paralelo al backend):
 *   nombreLargo       — SAT/oficial. Para títulos formales y subtítulos.
 *   nombreCorto       — Cuando no cabe el largo. Chip, badge, breadcrumb.
 *   fraccion          — Fracción LFPIORPI aplicable (V, V Bis, XV, etc.).
 *   articulo          — Cita legal (Art. 17 Fr. V, etc.).
 *   bitacoraCategoria — Prefijo para categoría de bitácora.
 */
export const GIRO_METADATA = {
  inmobiliaria: {
    nombreLargo: "Intermediación y comercialización de bienes inmuebles",
    nombreCorto: "Intermediación de inmuebles",
    fraccion: "V",
    articulo: "Art. 17 Fr. V",
    bitacoraCategoria: "Intermediación de inmuebles",
  },
  joyeria: {
    nombreLargo: "Comercialización de metales y piedras preciosas, joyas y relojes",
    nombreCorto: "Metales y piedras preciosas",
    fraccion: "VI",
    articulo: "Art. 17 Fr. VI",
    bitacoraCategoria: "Metales y piedras preciosas",
  },
  arte: {
    nombreLargo: "Subasta y comercialización de obras de arte",
    nombreCorto: "Obras de arte",
    fraccion: "VII",
    articulo: "Art. 17 Fr. VII",
    bitacoraCategoria: "Obras de arte",
  },
  vehiculos: {
    nombreLargo: "Comercialización y distribución de vehículos aéreos, marítimos y terrestres",
    nombreCorto: "Vehículos",
    fraccion: "VIII",
    articulo: "Art. 17 Fr. VIII",
    bitacoraCategoria: "Vehículos",
  },
  juegos_sorteos: {
    nombreLargo: "Juegos con apuesta, concursos y sorteos",
    nombreCorto: "Juegos y sorteos",
    fraccion: "I",
    articulo: "Art. 17 Fr. I",
    bitacoraCategoria: "Juegos y sorteos",
  },
  tarjetas_servicios: {
    nombreLargo:
      "Emisión y comercialización de tarjetas de crédito, de servicios, prepagadas, vales y monederos electrónicos",
    nombreCorto: "Tarjetas y vales",
    fraccion: "II",
    articulo: "Art. 17 Fr. II",
    bitacoraCategoria: "Tarjetas y vales",
  },
  cheques_viajero: {
    nombreLargo: "Comercialización habitual o profesional de cheques de viajero",
    nombreCorto: "Cheques de viajero",
    fraccion: "III",
    articulo: "Art. 17 Fr. III",
    bitacoraCategoria: "Cheques de viajero",
  },
  mutuo_prestamo: {
    nombreLargo:
      "Servicios de mutuo, garantía u otorgamiento de préstamos o créditos por sujetos distintos a las entidades financieras",
    nombreCorto: "Mutuo, préstamo o crédito",
    fraccion: "IV",
    articulo: "Art. 17 Fr. IV",
    bitacoraCategoria: "Mutuo, préstamo o crédito",
  },
  blindaje: {
    nombreLargo: "Servicios de blindaje de vehículos e inmuebles",
    nombreCorto: "Blindaje",
    fraccion: "IX",
    articulo: "Art. 17 Fr. IX",
    bitacoraCategoria: "Blindaje",
  },
  traslado_valores: {
    nombreLargo: "Traslado o custodia de dinero o valores",
    nombreCorto: "Traslado de valores",
    fraccion: "X",
    articulo: "Art. 17 Fr. X",
    bitacoraCategoria: "Traslado de valores",
  },
  servicios_profesionales: {
    nombreLargo: "Servicios profesionales independientes",
    nombreCorto: "Servicios profesionales",
    fraccion: "XI",
    articulo: "Art. 17 Fr. XI",
    bitacoraCategoria: "Servicios profesionales",
  },
  fe_publica: {
    nombreLargo: "Fe pública — notarios y corredores públicos",
    nombreCorto: "Fe pública",
    fraccion: "XII",
    articulo: "Art. 17 Fr. XII",
    bitacoraCategoria: "Fe pública",
  },
  donativos: {
    nombreLargo: "Recepción de donativos o aportaciones por organizaciones sin fines de lucro",
    nombreCorto: "Donativos",
    fraccion: "XIII",
    articulo: "Art. 17 Fr. XIII",
    bitacoraCategoria: "Donativos",
  },
  comercio_exterior: {
    nombreLargo:
      "Servicios de comercio exterior como agente o apoderado aduanal, agencia aduanal o despacho",
    nombreCorto: "Comercio exterior",
    fraccion: "XIV",
    articulo: "Art. 17 Fr. XIV",
    bitacoraCategoria: "Comercio exterior",
  },
  activos_virtuales: {
    nombreLargo: "Intercambio de activos virtuales",
    nombreCorto: "Activos virtuales",
    fraccion: "XVI",
    articulo: "Art. 17 Fr. XVI",
    bitacoraCategoria: "Activos virtuales",
  },
};

const _FALLBACK = {
  nombreLargo: "Actividad vulnerable",
  nombreCorto: "Actividad vulnerable",
  fraccion: "",
  articulo: "LFPIORPI Art. 17",
  bitacoraCategoria: "Actividad vulnerable",
};

/**
 * `true` si el giro cae bajo LFPIORPI Art. 17. Case-insensitive; tolera null/undefined.
 * Uso típico: `esActividadVulnerable(user?.giro)`.
 */
export function esActividadVulnerable(giro) {
  if (!giro) return false;
  return ACTIVIDADES_VULNERABLES.includes(String(giro).trim().toLowerCase());
}

/** Devuelve el diccionario de metadata del giro. Fallback razonable si no está catalogado. */
export function metadataGiro(giro) {
  if (!giro) return _FALLBACK;
  return GIRO_METADATA[String(giro).trim().toLowerCase()] || _FALLBACK;
}

/** Nombre SAT/oficial. Ej: "Intermediación y comercialización de bienes inmuebles". */
export function giroNombreLargo(giro) { return metadataGiro(giro).nombreLargo; }

/** Nombre corto para chips/badges. Ej: "Intermediación de inmuebles". */
export function giroNombreCorto(giro) { return metadataGiro(giro).nombreCorto; }

/** Fracción LFPIORPI del giro. Ej: "V", "VI". */
export function giroFraccion(giro) { return metadataGiro(giro).fraccion; }

/** Cita legal del giro. Ej: "Art. 17 Fr. V". */
export function giroArticulo(giro) { return metadataGiro(giro).articulo; }

/**
 * Etiqueta legible del régimen supervisor (usada en badges y avisos).
 */
export function regimenSupervisor(giro) {
  return esActividadVulnerable(giro) ? "LFPIORPI (SAT-UIF)" : "CNBV";
}
